import { createTheme, GlobalStyles } from '@mui/material';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { APIProvider } from '@vis.gl/react-google-maps';
import { message, Modal } from 'antd';
import ConfigProvider from 'antd/es/config-provider';
import vi_VN from 'antd/es/locale/vi_VN';
import theme from 'antd/es/theme';
import favicon from 'assets/favicons/va-favicon.ico';
import DOMPurify from 'dompurify';
import {
  appPrimaryColorSelector,
  darkModeSelector,
} from 'features/app-ui/selectors';
import {
  changeSidebarCollapsedState,
  setDarkMode,
} from 'features/app-ui/slice';
import { useAppDispatch, useAppSelector } from 'lib/stores/hook';
import { Suspense, useCallback, useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { AppRouterConfig } from 'routes/AppRouterConfig';
import { ScrollRestoration } from 'shared/components/ScrollRestoration';
import { SplashScreen } from 'shared/components/SplashScreen';
import { messageUtils, modalUtils } from 'shared/utils';
import { darkThemeOptions, themeOptions } from 'styles/MuiTheme';
import { ENV } from 'utils/env';
DOMPurify.addHook('afterSanitizeAttributes', function (node) {
  if ('target' in node) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

const App = () => {
  const dispatch = useAppDispatch();
  const appPrimaryColor = useAppSelector(appPrimaryColorSelector);
  const isDarkMode = useAppSelector(darkModeSelector);

  const MuiTheme = createTheme(isDarkMode ? darkThemeOptions : themeOptions);

  const [messageApi, messageContext] = message.useMessage();
  messageUtils.message = messageApi;

  const [modal, modalContext] = Modal.useModal();
  modalUtils.modal = modal;

  const setAppFavicon = useCallback(() => {
    const link = document.createElement('link');
    const oldLink = document.getElementById('dynamic-favicon');

    link.id = 'dynamic-favicon';
    link.rel = 'icon';
    link.href = favicon;

    if (oldLink) {
      document.head.removeChild(oldLink);
    }
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    setAppFavicon();
    dispatch(setDarkMode(localStorage.getItem('darkMode') === 'true'));
    dispatch(
      changeSidebarCollapsedState(
        localStorage.getItem('compactSidebar') === 'true'
      )
    );
  }, [dispatch, setAppFavicon]);

  return (
    <APIProvider apiKey={ENV.VITE_APP_GM_API_KEY}>
      <ThemeProvider theme={MuiTheme}>
        <StyledEngineProvider injectFirst>
          <GlobalStyles
            styles={() => ({
              '.MuiButton-icon': {
                svg: {
                  fontSize: '16px !important',
                },
              },
            })}
          />
          <ConfigProvider
            locale={vi_VN}
            theme={{
              algorithm: isDarkMode
                ? theme.darkAlgorithm
                : theme.defaultAlgorithm,
              token: {
                colorPrimary: appPrimaryColor,
                colorSuccess: '#059669',
                colorWarning: '#FFAB00',
                colorError: '#DE4D4D',
                colorInfo: '#00B8D9',
                borderRadius: 8,
              },
              components: {
                Button: {
                  controlHeight: 32,
                  borderRadius: 8,
                  contentFontSize: 12,
                },
              },
            }}
          >
            <Suspense
              fallback={
                <ScrollRestoration>
                  <SplashScreen fullScreen />
                </ScrollRestoration>
              }
            >
              {messageContext}
              {modalContext}
              <RouterProvider router={AppRouterConfig} />
            </Suspense>
          </ConfigProvider>
        </StyledEngineProvider>
      </ThemeProvider>
    </APIProvider>
  );
};

export default App;
